import React, { useEffect, useState } from "react";
import { Grid2 } from "@mui/material";
import styles from "./SiteDetails.module.scss";
import { useLocation, useNavigate } from "react-router-dom";

// Components Import Section
import SiteHeaderCard from "../../Components/HeaderComponent/Header";
import IconDetails from "../../Components/IconDetails/IconDetails";
import LicenseUsage from "../../../../AdminComponents/LicenseUsage/LicenseUsage";
import { ReactComponent as Start } from "../../../../../../GlobalAssets/svg/StartUp.svg";

// Redux and Utils Section
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import { useSelector } from "react-redux";
import {
  adminApi,
  FEATURES,
  getIconDetailsData,
  lastSegmentPath,
  STAGING,
} from "../../../../../../Utils/roconConstants";
import {
  getStagingEnv,
  staging,
  siteDetails,
  prod,
} from "../../../../../../Utils/sitePages";
import { getUsage } from "../../../../../../Utils/utils";
import PageLoading from "../../../../../../GlobalComponents/PageLoading/PageLoading";
import { useParams } from "react-router-dom";
import ModalPopup from "../../../../../../GlobalComponents/Popup/ModalPopup";
import FormPopup from "../../../../../../GlobalComponents/FormPopup/FormPopup";
import {
  ActivatePopup,
  isSiteDeletionInProgress,
} from "../../../../../../Utils/activatePopup";

export default function SiteDetails() {
  const [selectedUsageRange, setSelectedUsageRange] = useState("Month");
  const [isLoading, setIsLoading] = useState(true);
  const [versionPopup, setVersionPopup] = useState(false);
  const [deletionPopup, setDeletionPopup] = useState({
    popupState: false,
    popupType: "",
    popupTitle: "",
    popupSubtitle: "",
    popupButton: false,
    popupCancel: false,
    popupButtonTitle: "",
  });
  let sites = useSelector((state) => state?.sitesInfo?.selectedSite);
  const isDedicated = sites?.dedicatedInstanceDetails;
  let organizationsInfo = useSelector(
    (state) => state.organizationsInfo.selectedOrg
  );
  let plan = useSelector(
    (state) => state.plansInfo?.specificPlan?.specificPlans[0]
  );

  const selectedDomain = useSelector((state) => state.sitesInfo.selectedDomain);

  const graphData = useSelector(
    (state) => state?.sitesInfo?.graphDetails?.data
  );
  const isStaging = staging(sites);

  const isDevOn = sites?.stagingDomainsList?.find(
    (item) => item.environment?.devEnv === true
  );

  const navigate = useNavigate();
  const location = useLocation();
  const lastSegment = lastSegmentPath(location);

  const params = useParams();
  const orgId = params.orgId;
  const siteId = params.siteId;
  const isEbsSite =
    sites?.ebsSite === true && String(sites?.siteId) === String(siteId);

  useEffect(() => {
    getStore().dispatch({
      type: Actions.API_GET_SELECTED_SITE_DETAILS,
      data: {
        siteId: siteId,
      },
      meta: {
        onSuccess: (data) => {
          const selectedEnvData = data.stagingDomainsList.find(
            (list) =>
              list.environment ===
              (selectedDomain?.environment === "STAGING" ? "STAGING" : "LIVE")
          );
          getStore().dispatch({
            type: Actions.SET_SELECTED_DOMAIN,
            data: selectedEnvData,
          });
          setIsLoading(false);
        },
        onError: () => {
          setIsLoading(false);
        },
      },
    });

    if (!organizationsInfo) {
      getStore().dispatch({ type: Actions.API_ORG_DASHBOARD });
    }

    getStore().dispatch({
      type: Actions.SET_SITE_DETAILS,
      data: {
        orgId: orgId,
        siteId: siteId,
        sitePage: lastSegment,
      },
    });
  }, [siteId, selectedDomain?.environment]);

  useEffect(() => {
    fetchData();
  }, [selectedUsageRange]);

  // Last line of defence: whichever page linked here (sites overview, dashboard,
  // plan usage, a pasted URL), a site that is being deleted must not be opened.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isSiteDeletionInProgress(siteId)) {
      ActivatePopup("site-deletion-in-progress", setDeletionPopup);
    }
  }, [isLoading, siteId, sites?.deletionInProgress]);

  const leaveDeletedSite = () => {
    setDeletionPopup({ popupState: false });
    navigate(`/account/${orgId}/sites-overview`);
  };

  const fetchData = () => {
    getStore().dispatch({
      type: Actions.API_GRAPH_DETAILS,
      data: {
        siteId: siteId,
        orgId: orgId ? orgId : -1,
        timeInterval: selectedUsageRange.toLowerCase(),
      },
    });
  };

  const planFeatures = plan?.featureDetails;
  const siteData = siteDetails(sites);
  const wpLatest =
    selectedDomain?.environment === "STAGING"
      ? selectedDomain.wpLatestVersion
      : siteData.staging[0]?.latestWp;
  const wpVersion =
    selectedDomain?.environment === "STAGING"
      ? selectedDomain.wpVersion
      : siteData.staging[0]?.wp;
  const dedicatedHosting = useSelector(
    (state) => state.plansInfo?.specificPlan?.isDedicated
  );

  const handleVersionPopup = () => setVersionPopup(!versionPopup);

  const iconDetailsData = getIconDetailsData(
    siteData,
    parseFloat(wpVersion),
    parseFloat(wpLatest),
    handleVersionPopup,
    dedicatedHosting
  );

  let livedom = siteData?.domain.find((item) => item.primary === "Y")?.name;

  function updateWpVersion() {
    setIsLoading(true);
    getStore().dispatch({
      type: Actions.API_WP_VERSION_UPDATE,
      data: {
        siteId: siteData?.siteId,
        version: wpLatest,
        stagingDomainName:
          selectedDomain?.environment === "STAGING"
            ? selectedDomain.domainName
            : siteData?.siteName,
      },
      meta: {
        onSuccess: () => {
          setVersionPopup(false);
          setIsLoading(false);
        },
        onError: () => {
          setVersionPopup(false);
          setIsLoading(false);
        },
      },
    });
  }

  return (
    <>
      {!isLoading ? (
        <Grid2 className={styles.Container}>
          <Grid2 className={styles.header}>Site Details</Grid2>
          {siteData.siteName && (
            <SiteHeaderCard
              name={
                selectedDomain?.environment === "STAGING"
                  ? selectedDomain.domainName
                  : siteData.siteName
              }
              domain={
                livedom?.length > 0 &&
                (selectedDomain?.environment !== "STAGING" || isDedicated)
                  ? livedom
                  : (selectedDomain?.environment === "STAGING"
                      ? selectedDomain.domainName
                      : siteData.siteName) +
                    "." +
                    process.env.REACT_APP_TEMP_DOMAIN
              }
              development={isStaging}
              production={getStagingEnv(siteData.staging, STAGING.LIVE)}
              phpClick={() => adminApi("php")}
              wpClick={() =>
                adminApi(
                  "wp",
                  selectedDomain?.environment === "STAGING"
                    ? selectedDomain.domainName
                    : siteData.siteName,
                  selectedDomain?.environment === "STAGING"
                    ? selectedDomain.domainName
                    : livedom?.length > 0
                    ? livedom
                    : null,
                  selectedDomain?.environment === "STAGING" ? true : false
                )
              }
              domainClick={() =>
                adminApi(
                  "domain",
                  selectedDomain?.environment === "STAGING"
                    ? selectedDomain.domainName
                    : siteData.siteName,
                  selectedDomain?.environment === "STAGING"
                    ? selectedDomain.domainName
                    : livedom?.length > 0
                    ? livedom
                    : null,
                  selectedDomain?.environment === "STAGING" ? true : false
                )
              }
              siteId={siteData.siteId}
              orgId={orgId}
              image={siteData.imagePath ? siteData.imagePath : null}
              selectedCountry={organizationsInfo?.country}
              devEnv={isDevOn}
              orgInfo={organizationsInfo}
              addOnWaf={siteData.wafStatus}
              addOnPerformanceSite={siteData.performanceSite}
            />
          )}

          <Grid2 className={styles.gridContainer}>
            {iconDetailsData
              .filter(
                (item) =>
                  !(
                    (selectedDomain?.environment === "STAGING" || isEbsSite) &&
                    item.title === "IP Address"
                  )
              )
              .map((item, index) => (
                <Grid2 item key={index} className={styles.gridItem}>
                  <IconDetails
                    icon={item.icon}
                    title={item.title}
                    subTitle={item.subTitle}
                    copy={item.copy}
                  />
                </Grid2>
              ))}
          </Grid2>

          {siteData && (
            <LicenseUsage
              bandwidthGraphResponse={graphData?.bandwidthGraphResponse}
              storageGraphResponse={graphData?.storageGraphResponse}
              visitsGraphResponse={graphData?.visitsGraphResponse}
              maxBandwidth={
                getUsage(
                  isEbsSite ? FEATURES.BANDWIDTH : "STORAGE",
                  planFeatures
                )?.limitOrAvailablity
              }
              setSelectedUsageRange={setSelectedUsageRange}
              {...(isEbsSite && {
                visitsLabel: "Requests",
                visitsTooltip: "CloudFront requests, not unique visitors",
              })}
            />
          )}
        </Grid2>
      ) : (
        <PageLoading />
      )}
      <ModalPopup
        trigger={versionPopup}
        backPopup={"Cancel"}
        backOnClick={() => setVersionPopup(false)}
        nextpopup={"Update"}
        header={"WP Version Update"}
        nextOnClick={() => updateWpVersion()}
        loading={isLoading}
      >
        <Grid2 className={styles.center}>
          <Start style={{ height: 160, width: 160 }} />

          <Grid2 className={styles.desc}>
            The latest version of WordPress has been released, upgrading from
            <span className={styles.colorVersion}> {wpVersion} </span>to
            <span className={styles.colorVersion}> {wpLatest}</span>. We
            recommend updating your WordPress installation to the latest version
            for improved performance and security.
          </Grid2>
        </Grid2>
      </ModalPopup>
      <FormPopup
        trigger={deletionPopup.popupState}
        setTrigger={setDeletionPopup}
        type={deletionPopup.popupType}
        title={deletionPopup.popupTitle}
        subtitle={deletionPopup.popupSubtitle}
        button={deletionPopup.popupButton}
        cancel={deletionPopup.popupCancel}
        buttonTitle={deletionPopup.popupButtonTitle}
        buttonFunction={leaveDeletedSite}
      />
    </>
  );
}
